/**
 * ViewportVisibility JS controller.
 */

// Use ScrollTrigger to detect when elements come into viewport.
import ScrollTrigger from 'scrolltrigger-classes';

/**
 * Class LocationsControl
 */
class ViewportVisibility {

    /**
     * Class constructor is for binding class properties.
     */
    constructor() {
    }

    /**
     * This functions is run after all script classes have been instantiated.
     * Use this if you need to access some other script instances before the document ready event.
     */
    init() {

        // Define customs functions that run when elements with them as a scrollTrigger callback come into view.
        // https://github.com/terwanerik/ScrollTrigger/blob/master/README.md#callbacks
        // 
        // Unfortunately things like this do no work in ScrollTrigger: `data-scroll-showCallback="Theme.ViewportVisibility.showLazyImage()`.
        // Probably because scrolltrigger would try to run them like this `this.scrollTrigger.callScope[Theme.ViewportVisibility.showLazyImage].apply( trigger.element, params )`
        //
        // Defining a custom callscope will not work either, since it is not available when scrollTrigger initializes. Custom callscope can't be defined as an option.

        window.DPTLazyImageCb = function() {

            // `this` refers to the html element coming into the viewport
            const el = this;

            Theme.ViewportVisibility.showLazyImage( el );
        };

        window.DPTSomeFeedShowCb = function() {

            // `this` refers to the html element coming into the viewport
            const el = this;

            Theme.SomeFeed.DPTLazySomeFeed( el );
        };
    }

    /** 
     * Cache dom elements for use in the class's methods
     */
    cache() {

        // Get all elements watched by ScrollTrigger. Alternatively the selector could be `js-lazyload-img` to only get
        // lazy load images.
        this.scrollTriggerElements = document.querySelectorAll( '[data-scroll]' );
    }

    /**
     * Set up ScrollTrigger.
     * @link https://github.com/terwanerik/ScrollTrigger 
     */
    setUpScrollTrigger() {

        this.scrollTrigger = new ScrollTrigger({
            toggle: {
                visible: 'js-st-visible',
                hidden: 'js-st-invisible'
            }
        });

        // Manually run the ScrollTrigger update method once since in some cases ScrollTrigger misses something on
        // the initial load. This is usually if there are elements with offset setting very high or low in the
        // viewport. This should not be needed if he issue https://github.com/terwanerik/ScrollTrigger/issues/49 gets
        // fixed.
        this.scrollTrigger.scrollDidChange();

        // Just run one more check for the elements that ScrollTrigger might have missed. This should not be needed if 
        // the issue https://github.com/terwanerik/ScrollTrigger/issues/49 gets fixed.
        // Setting a timeout to make sure that the browser has been given enough time to scroll to the correct position
        // in case the user has reloaded the page returned to it with back-functionality.
        setTimeout( () => {
            this.checkScrollTriggerElements();
        }, 1000 );
    }

    /**
     * Checks if any of the scrollTrigger elements are currently in the viewport. If they are, change classes and run
     * callbacks. This is more or less the same code as in ScrollTrigger.js
     *
     * @memberof ViewportVisibility
     */
    checkScrollTriggerElements() {
        Array.prototype.forEach.call( this.scrollTriggerElements, ( element ) => {

            // Check if any part of the element is in the viewport.
            if ( this.isVisible( element ) ) {

                // Get the scrollTrigger object of the element.
                const trigger = this.scrollTrigger.triggerFor( element );

                // Run the class adding and removal functions. Copied from ScrollTriggrer.js
                if ( trigger ) {

                    // Add class to element and run possible callback
                    trigger.addClass( trigger.visibleClass, () => {
                        if ( trigger.showCallback ) {
                            this.functionCall( trigger, trigger.showCallback );
                        }
                    });

                    trigger.removeClass( trigger.hiddenClass );

                    // Remove the Trigger object for the given element if it is a "once" object. 
                    if ( trigger.once ) {
                        this.scrollTrigger.destroy( element );
                    }
                }
            }
        });
    }

    /**
     * Check if any part of an element is in the viewport.
     * Note that this appears to have some unexpected behavior in IE and Edge if not scrolled to the top on docReady.
     * @param {object} el Element to check.
     */
    isVisible( el ) {
      const { top, bottom } = el.getBoundingClientRect();
      const vHeight = ( window.innerHeight || document.documentElement.clientHeight );

      return (
        ( top > 0 || bottom > 0 ) &&
        top < vHeight
      );
    }

    /**
     * Function needed by ScrollTrigger to run callback functions. Copied from ScrollTrigger.js
     * @param {object} trigger ScrollTrigger object.
     * @param {string} functionAsString Defined callback function..
     */
    functionCall( trigger, functionAsString ) {
        var params = functionAsString.split( '(' );
        var method = params[0];

        if ( params.length > 1 ) {
            params = params[1].split( ')' )[0]; // get the value between the parentheses

            // check if there are multiple attributes
            if ( params.indexOf( '\', \'' ) > -1 ) {
                params = params.split( '\', \'' );
            } else if ( params.indexOf( '\',\'' ) > -1 ) {
                params = params.split( '\',\'' );
            } else if ( params.indexOf( '", "' ) > -1 ) {
                params = params.split( '", "' );
            } else if ( params.indexOf( '","' ) > -1 ) {
                params = params.split( '","' );
            } else {

                // nope, just a single parameter
                params = [ params ];
            }
        } else {
            params = [];
        }

        // remove all quotes from the parameters
        params = params.map( ( param ) => {
            return this.removeQuotes( param );
        });

        if ( typeof this.scrollTrigger.callScope[method] == 'function' ) {

            // function exists in the call scope so let's try to call it. Some methods don't like to have the HTMLElement
            // passed as 'this', so retry without that if it fails.
            try {
                this.scrollTrigger.callScope[method].apply( trigger.element, params );
            } catch ( e ) {

                // alright let's try again
                try {
                    this.scrollTrigger.callScope[method].apply( null, params );
                } catch ( e ) {

                    // ah to bad.
                }
            }
        }
    }

    /**
     * Removes quotes from a string, e.g. turns 'foo' or "foo" into foo. Copied from ScrollTrigger.js.
     * @param {string} str String to remove quotes from.
     */
    removeQuotes( str ) {
        str = str + ''; // force a string

        if ( str[0] == '"' ) {
            str = str.substr( 1 );
        }

        if ( str[0] == '\'' ) {
            str = str.substr( 1 );
        }

        if ( str[str.length - 1] == '"' ) {
            str = str.substr( 0, str.length - 1 );
        }

        if ( str[str.length - 1] == '\'' ) {
            str = str.substr( 0, str.length - 1 );
        }

        return str;
    }

    /**
     * Reveal an lazyload image. Updates image srscet and classnames.
     * @param {object} el The image element to update.
     */
    static showLazyImage( el ) {

        // Replace content of srcset with content of data-srcset.
        const dataSrcSet = el.getAttribute( 'data-srcset' );
        if ( dataSrcSet && el.classList.contains( 'js-lazyload-img' ) ) {
            el.setAttribute( 'srcset', dataSrcSet );

            // Update class. This tells possible other functions that the lazy-handling has been done.
            el.classList.add( 'js-lazyload-img-loaded' );
            el.classList.remove( 'js-lazyload-img' );
            return;
        }
    }

    /**
     * Run when the document is ready.
     */
    docReady() {
        this.cache();
        this.setUpScrollTrigger();
    }
}

// Export the class reference.
module.exports = ViewportVisibility;



// WEBPACK FOOTER //
// ./assets/scripts/viewport-visibility.js